'use client';
import LinkWrapper from '../link-wrapper/link-wrapper';
import {
    hasCldImage,
    TypeBlockPeopleList,
    TypePagePerson,
    TypePageWithoutUnresolvedLink,
} from '@fm-nx/contentful-types';
import { CldImage } from 'next-cloudinary';

type ListItemPersonProps = {
    personPage: TypePagePerson<'WITHOUT_UNRESOLVABLE_LINKS'>;
    page: TypePageWithoutUnresolvedLink;
    mode?: 'dark' | 'light';
};

const ListItemPerson = (props: ListItemPersonProps) => {
    const { personPage, page, mode = 'light' } = props;
    const { person } = personPage.fields;
    if (!person) return null;

    const { firstName, lastName, jobTitle } = person.fields;
    const textColor = mode === 'dark' ? 'text-white' : '';
    const imageCloudinary = hasCldImage(person.fields.profileImage?.fields)
        ? person.fields.profileImage?.fields.cloudinaryMedia[0]
        : null;

    return (
        <LinkWrapper page={page} className="group">
            <div className="flex flex-col items-center justify-center text-center">
                {imageCloudinary && (
                    <div className="relative max-w-65">
                        <CldImage
                            className="object-cover rounded-full aspect-square"
                            src={`${imageCloudinary.public_id}`}
                            alt={person.fields.profileImage?.fields.altText || `${firstName} ${lastName}`}
                            crop="fill"
                            width="400"
                            height="400"
                        />
                    </div>
                )}
                <h5 className={`mt-6 h-md border-b-out ${textColor}`}>{`${firstName} ${lastName}`}</h5>
                <div className={`mt-6 overline h5 ${textColor}`}>{jobTitle}</div>
            </div>
        </LinkWrapper>
    );
};

type PeopleListProps = {
    blockPeopleList: TypeBlockPeopleList<'WITHOUT_UNRESOLVABLE_LINKS'>;
    mode?: 'dark' | 'light';
    context?: 'default' | 'dark-circle';
};

export const PeopleList = (props: PeopleListProps) => {
    const { blockPeopleList, mode = 'light', context = 'default' } = props;
    const { fields } = blockPeopleList;
    const { peoplePages } = fields;

    // TODO: add loader
    if (!peoplePages) return null;

    const isDarkCircle = context === 'dark-circle';
    const outerWrapperClasses = isDarkCircle ? 'flex justify-center' : 'grid grid-cols-14';
    const innerWrapperClasses = isDarkCircle ? 'w-full max-w-5xl' : 'col-span-12 col-start-2';
    const gridClasses = isDarkCircle
        ? 'flex flex-wrap justify-center gap-8 md:gap-28'
        : 'block md:grid-cols-12 md:gap-6 md:grid';
    const itemClasses = isDarkCircle ? 'mb-8 w-full md:w-auto md:max-w-[280px]' : 'mb-8 md:col-span-6 lg:col-span-4';

    return (
        <div className="w-full mx-auto mb-20 2xl:mb-32 max-w-container">
            <div className={outerWrapperClasses}>
                <div className={innerWrapperClasses}>
                    <div className={gridClasses}>
                        {peoplePages.map((page) => {
                            if (!page) return null;
                            return (
                                <div className={itemClasses} key={page.sys.id}>
                                    <ListItemPerson personPage={page} page={page} mode={mode} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PeopleList;
